import { useCallback, useMemo } from 'react'
import { InteractionModel } from 'types/shared'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch } from 'state/store'
import { interactionModelOperations } from '../redux'
import useSaveInteraction from 'utils/useSaveInteraction'
import debounce from 'lodash.debounce'

type SaveDraftParams = {
  interactionModel: InteractionModel
}

const useSaveDraftInteraction = () => {
  const dispatch = useAppDispatch()

  const [searchParams, setSearchParams] = useSearchParams()
  const existingDraftUuid = searchParams.get('draft_interaction_uuid') || ''

  const { createInteraction, updateInteraction } = useSaveInteraction()

  const createDraftInteraction = useCallback(
    async ({ interactionModel }: SaveDraftParams) => {
      try {
        dispatch(interactionModelOperations.updateDraftRequest())

        const response = await createInteraction({
          interactionModel,
          showLoading: false,
          activate: false
        })

        const draftInteractionUuid =
          response.data?.new_interaction_ids?.[0] || ''

        if (draftInteractionUuid) {
          setSearchParams(
            (oldSearchParams) => {
              oldSearchParams.set(
                'draft_interaction_uuid',
                draftInteractionUuid
              )
              return oldSearchParams
            },
            { replace: true }
          )
          dispatch(interactionModelOperations.initializeDraft())
        }
      } catch (e) {
        console.error('Failed to create draft interaction:', e)
        dispatch(interactionModelOperations.updateDraftFailure())
      }
    },
    [createInteraction, setSearchParams, dispatch]
  )

  const updateDraftInteraction = useCallback(
    async ({ interactionModel }: SaveDraftParams) => {
      try {
        dispatch(interactionModelOperations.updateDraftRequest())

        await updateInteraction({
          draftUuid: existingDraftUuid,
          interactionModel,
          showLoading: false,
          activate: false
        })

        const newVersion = Number(interactionModel.draftVersion) + 1

        dispatch(
          interactionModelOperations.updateDraftSuccess(newVersion.toString())
        )
      } catch (e) {
        console.error('Failed to update draft interaction:', e)
        dispatch(interactionModelOperations.updateDraftFailure())
      }
    },
    [updateInteraction, dispatch, existingDraftUuid]
  )

  const debouncedUpdateDraftInteraction = useMemo(
    () => debounce(updateDraftInteraction, 500),
    [updateDraftInteraction]
  )

  return {
    createDraftInteraction,
    updateDraftInteraction: debouncedUpdateDraftInteraction
  }
}

export default useSaveDraftInteraction
